import { action } from '@ember/object';
import { next, scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { MD_BREAKPOINT } from 'webapp/utils/common';
import { getWidescreenSizeThatFits } from 'webapp/utils/html';

const enum ViewMode {
	SM = 'sm', // < MD_BREAKPOINT (no changes atm, was used for insights area)
	LG = 'lg', // everything else
}

export default class ReportUIHelper {
	@tracked isSlideNavOpen: boolean;
	@tracked viewMode: ViewMode = ViewMode.LG;

	@tracked isSlideDescriptionOpen?: boolean;
	@tracked isSlideDescriptionExpanded?: boolean;
	@tracked isSlideDescriptionAvailable?: boolean;

	mmReportsService: Service.ModelManager.Reports;
	eventManagerService: Service.EventManager;

	constructor(
		mmReportsService: Service.ModelManager.Reports,
		eventManagerService: Service.EventManager,
	) {
		this.mmReportsService = mmReportsService;
		this.eventManagerService = eventManagerService;
		this.isSlideNavOpen = true;
		this.checkViewMode();
		this.bindUpdateSlideSizeEvents();
	}

	get isViewModeSm() {
		return this.viewMode === ViewMode.SM;
	}

	get isViewModeLg() {
		return this.viewMode === ViewMode.LG;
	}

	get slideSwitchToggleIconName() {
		return this.isViewModeLg
			? this.isSlideNavOpen
				? 'ChevronLeft'
				: 'ChevronRight'
			: this.isSlideNavOpen
				? 'ChevronUp'
				: 'ChevronDown';
	}

	bindUpdateSlideSizeEvents() {
		// resize (only when in reports viewer or public url, thats why unbind on destroy)
		$(window).on('resize.slideContainer', this.scheduleUpdateSlideSize);

		// comments open
		this.eventManagerService.on(
			AppEvent.REPORT_VIEWER_DESCRIPTION_OPEN_TOGGLED,
			this,
			(eventData) => {
				const newValue = eventData.newValue;
				const oldValue = this.isSlideDescriptionOpen;

				if (newValue !== oldValue) {
					next(() => {
						this.isSlideDescriptionOpen = newValue;

						if (oldValue !== undefined) {
							// Skip on init, other events will calculate slide size
							this.scheduleUpdateSlideSize();
						}
					});
				}
			},
		);

		// comments expanded
		this.eventManagerService.on(
			AppEvent.REPORT_VIEWER_DESCRIPTION_EXPANDED_TOGGLED,
			this,
			(eventData) => {
				const newValue = eventData.newValue;
				const oldValue = this.isSlideDescriptionExpanded;

				if (newValue !== oldValue) {
					next(() => {
						this.isSlideDescriptionExpanded = newValue;

						if (oldValue !== undefined) {
							// Skip on init, other events will calculate slide size
							this.scheduleUpdateSlideSize();
						}
					});
				}
			},
		);
	}

	updateSlideSize(): void {
		const node: HTMLElement | null = document.body.querySelector('.report_slide_ct');
		const parentNode = node?.parentNode as HTMLElement | null;
		if (node && parentNode) {
			const size = getWidescreenSizeThatFits(
				parentNode.offsetWidth,
				parentNode.offsetHeight,
				(node.nextElementSibling as HTMLElement)?.clientHeight ?? 0,
			);
			node.style.maxWidth = size.width + 'px';
			node.style.height = size.height + 'px';
		}
	}

	@action
	scheduleUpdateSlideSize(): void {
		scheduleOnce('afterRender', this, this.updateSlideSize);
	}

	setSlideDescriptionAvailable(newValue: boolean) {
		const oldValue = this.isSlideDescriptionAvailable;

		if (newValue !== oldValue) {
			this.isSlideDescriptionAvailable = newValue;

			if (this.isSlideDescriptionOpen && oldValue !== undefined) {
				this.scheduleUpdateSlideSize();
				this.mmReportsService.triggerInnerSlideResize();
			}
			// else - doesn't matter, AppEvent.REPORT_VIEWER_DESCRIPTION_OPEN_TOGGLED will do the calculation
		}
	}

	@action
	toggleSlideNav() {
		this.isSlideNavOpen = !this.isSlideNavOpen;
		this.scheduleUpdateSlideSize(); // run here, skip when constructor sets isSlideNavOpen, other events will calculate slide size

		document.body.classList.toggle('collapsed-slide-nav', !this.isSlideNavOpen);
		this.mmReportsService.triggerInnerSlideResize();
	}

	@action
	checkViewMode(): void {
		const width = window.innerWidth,
			oldViewMode = this.viewMode;
		let newViewMode: ViewMode;

		if (width < MD_BREAKPOINT) {
			newViewMode = ViewMode.SM;
		} else {
			newViewMode = ViewMode.LG;
		}

		if (oldViewMode !== newViewMode) {
			this.viewMode = newViewMode;
			this.mmReportsService.triggerInnerSlideResize();
		}
	}

	destroy() {
		$(window).off('resize.slideContainer', this.scheduleUpdateSlideSize);
		this.eventManagerService.off(AppEvent.REPORT_VIEWER_DESCRIPTION_OPEN_TOGGLED, this);
		this.eventManagerService.off(AppEvent.REPORT_VIEWER_DESCRIPTION_EXPANDED_TOGGLED, this);
	}
}
